/**
 * WalletConnect v2 封装。
 *
 * - 懒加载 @walletconnect/ethereum-provider，常规用户不被 WC bundle 拖慢。
 * - 单例：实例在全局复用，会话可跨页面保持。
 * - 暴露 EIP-1193 兼容 provider，调用方使用方式与 window.ethereum 完全一致。
 *
 * @see https://docs.reown.com/advanced/multichain/multichain-walletconnect-v2
 */

const PROJECT_ID = '29916874a59958e9b395225b4e6af6de'

const APP_METADATA = {
  name: 'Aura DApp',
  description: 'Aura DApp Trading Platform',
  url: typeof window !== 'undefined' ? window.location.origin : 'https://www.aura-crypto.com',
  icons: typeof window !== 'undefined' ? [window.location.origin + '/favicon.ico'] : []
}

// 仅以太坊主网（chainId=1）；后续扩链只需追加 optionalChains。
const OPTIONAL_CHAINS = [1]

let initPromise = null
let providerInstance = null
let connectInFlight = null

async function lazyImportWcModule() {
  return await import(/* webpackChunkName: "walletconnect" */ '@walletconnect/ethereum-provider')
}

/**
 * 仅初始化（不弹连接 UI）。幂等。
 */
export async function initWalletConnectProvider() {
  if (providerInstance) return providerInstance
  if (initPromise) return initPromise

  initPromise = (async () => {
    console.log('[WC] loading @walletconnect/ethereum-provider...')
    const t0 = Date.now()
    const mod = await lazyImportWcModule()
    const EthereumProvider = mod.EthereumProvider || (mod.default && mod.default.EthereumProvider)
    if (!EthereumProvider) {
      throw new Error('EthereumProvider export not found in @walletconnect/ethereum-provider')
    }
    console.log('[WC] module loaded in', Date.now() - t0, 'ms; calling init()')
    const t1 = Date.now()
    const provider = await EthereumProvider.init({
      projectId: PROJECT_ID,
      metadata: APP_METADATA,
      showQrModal: true,
      optionalChains: OPTIONAL_CHAINS,
      methods: [
        'eth_sendTransaction',
        'eth_signTransaction',
        'personal_sign',
        'eth_sign',
        'eth_signTypedData',
        'eth_signTypedData_v4',
        'wallet_switchEthereumChain',
        'wallet_addEthereumChain'
      ],
      events: ['chainChanged', 'accountsChanged']
    })
    console.log('[WC] init done in', Date.now() - t1, 'ms; existingSession=', !!provider.session)
    providerInstance = provider
    return provider
  })().catch((err) => {
    initPromise = null
    console.error('[WC] init failed:', err && err.message ? err.message : err)
    throw err
  })

  return initPromise
}

/**
 * 弹出 WC 连接 UI（或复用已有会话）。返回 EIP-1193 provider。
 * 若用户关闭弹窗会抛出错误。
 */
export async function connectViaWalletConnect() {
  const provider = await initWalletConnectProvider()

  if (provider.session && Array.isArray(provider.accounts) && provider.accounts.length > 0) {
    console.log('[WC] reusing existing session, accounts=', provider.accounts.length)
    return provider
  }

  if (connectInFlight) return connectInFlight

  connectInFlight = (async () => {
    console.log('[WC] opening connect modal...')
    try {
      await provider.connect()
      console.log('[WC] connected, accounts=', (provider.accounts || []).length, 'chainId=', provider.chainId)
      return provider
    } catch (e) {
      console.warn('[WC] connect rejected/cancelled:', e && e.message ? e.message : e)
      throw e
    }
  })().finally(() => {
    connectInFlight = null
  })

  return connectInFlight
}

/**
 * 仅当存在活跃会话时返回 WC provider，供同步解析路径使用。
 */
export function getActiveWalletConnectProvider() {
  if (
    providerInstance &&
    providerInstance.session &&
    Array.isArray(providerInstance.accounts) &&
    providerInstance.accounts.length > 0
  ) {
    return providerInstance
  }
  return null
}

/**
 * 断开当前 WC 会话并清缓存。无会话时安全返回。
 */
export async function disconnectWalletConnect() {
  if (!providerInstance) return
  try {
    if (providerInstance.session) {
      await providerInstance.disconnect()
      console.log('[WC] disconnected')
    }
  } catch (e) {
    console.warn('[WC] disconnect error:', e && e.message ? e.message : e)
  } finally {
    providerInstance = null
    initPromise = null
    connectInFlight = null
  }
}
